import { useState, useEffect, useCallback } from "react";
import GameBoard from "./GameBoard";
import GameLobby from "./GameLobby";
import Game from "./Types/Game";
import GameRule from "./Types/GameRule";
import { Socket } from "socket.io-client";
import { NamePrompt } from "./Popup";
import * as api from "./Api";
import useLocalStorage from "./useLocalStorage";

interface Props {
  game: Game;
  joinAsName?: string;
  socket: Socket;
  onLeave: (game: Game, name: string) => void;
  onStart: (game: Game) => void;
  onGameRulesChange: (game: Game, rules: GameRule) => void;
  onTimeLimitChange: (game: Game, isTimeLimitEnabled: boolean) => void;
  onRemovePlayer: (game: Game, name: string) => void;
  isSpectating: boolean;
}

const GameContainer = ({
  game,
  joinAsName,
  onLeave,
  onStart,
  onGameRulesChange,
  onTimeLimitChange,
  onRemovePlayer,
  socket,
  isSpectating,
}: Props) => {
  const [name, setNameInLocalStorage] = useLocalStorage("name", "Bob");
  const [savedName, setName] = useState<string>("");
  const [showNamePrompt, setShowNamePrompt] = useState<boolean>(true);
  const [nameErrorMessage, setNameErrorMessage] = useState<
    string | undefined
  >(undefined);


  const onNameSaved = useCallback(
    (name: string) => {
      setName(name);

      socket.emit("join", game.id, name);
    },
    [game.id, socket]
  );

  const handleOnSubmitName = useCallback(
    (name: string) => {
      api.joinGame(game, name, isSpectating).then(() => {
        setShowNamePrompt(false);
        setNameInLocalStorage(name);

        onNameSaved(name);
      }).catch((err) => {
          setNameErrorMessage("Unable to join game. Maybe name isn't unique?");
      });
    },
    [game, isSpectating, onNameSaved, setNameInLocalStorage]
  );

  useEffect(() => {
    if (joinAsName) {
      onNameSaved(joinAsName);
      setShowNamePrompt(false);
    }
  }, [joinAsName, onNameSaved]);

  const onLeaveGame = (game: Game, name: string) => {
    setName("");
    onLeave(game, name);
  };

  const getView = () => {
    switch (game.state) {
      case "started":
      case "won":
      case "lost":
        return (
          <GameBoard
            game={game}
            name={joinAsName || savedName}
            onRemovePlayer={onRemovePlayer}
            isSpectating={isSpectating}
          />
        );
      case "not started":
        return (
          <GameLobby
            game={game}
            name={name}
            onLeave={(game, name) => onLeaveGame(game, name)}
            onStart={(game) => onStart(game)}
            onGameRulesChange={onGameRulesChange}
            onTimeLimitChange={onTimeLimitChange}
          />
        );
      default:
        return <div />;
    }
  };

  return (
    <>
      {showNamePrompt && (
        <NamePrompt
          onSubmit={handleOnSubmitName}
          lastUsedName={name}
          names={game.players?.map((player) => player.name) ?? []}
          nameErrorMessage={nameErrorMessage}
        />
      )}
      {getView()}
    </>
  );
};

export default GameContainer;
